import { useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { EDU, track } from './eduClient';
import { authoringMode } from './eduSettings';
import { useFieldHelp } from './useFieldHelp';

/**
 * Per-field help affordance (F-ED-03): an info icon → popover with what / why / example, sourced from
 * `GET /v1/edu/field-help?family=`. Two degrade paths:
 *   - no family, or the endpoint is absent → renders NOTHING (icons simply don't appear);
 *   - the family's help loaded but THIS field has no entry → in authoring mode only, a visible
 *     "missing help" marker so the coverage gap is legible ("no help, no publish", not enforced here).
 * The trigger is a real button (keyboard-openable, aria-labelled); opening emits `edu.field_help.opened`.
 */
export function FieldHelp({ family, field }: { family?: string; field: string }) {
  const help = useFieldHelp(family);
  const [open, setOpen] = useState(false);

  if (!family) return null;
  const entry = help.lookup(field);

  if (!entry) {
    if (help.available && authoringMode()) {
      return (
        <Tooltip title="No field help authored yet — required before this field's capability can publish (F-ED-03: “no help, no publish”).">
          <WarningOutlined
            aria-label={`No help authored for ${field}`}
            style={{ fontSize: 11, color: 'var(--ol-attention)' }}
          />
        </Tooltip>
      );
    }
    return null;
  }

  const content = (
    <div className="ol-fieldhelp" role="note">
      <div className="ol-fieldhelp__row">
        <span className="ol-fieldhelp__label">What</span>
        <span>{entry.what}</span>
      </div>
      <div className="ol-fieldhelp__row">
        <span className="ol-fieldhelp__label">Why</span>
        <span>{entry.why}</span>
      </div>
      <div className="ol-fieldhelp__row">
        <span className="ol-fieldhelp__label">Example</span>
        <code className="ol-mono ol-fieldhelp__example">{entry.example}</code>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      placement="leftTop"
      overlayStyle={{ maxWidth: 320 }}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) track(EDU.fieldHelpOpened, { family, field });
      }}
    >
      <Button
        type="text"
        size="small"
        aria-label={`Help for ${field}`}
        icon={<InfoCircleOutlined />}
        style={{ width: 18, height: 18, minWidth: 18, padding: 0, color: 'var(--ol-primary)' }}
      />
    </Popover>
  );
}
